import { claimBarzaTaskExchange, getCheckStatus } from '../../../Services/Requests';
import { BarzhaClickAd, BarzhaClickResponse, TaskStatusItem } from '../types';


export class BarzhaClickService {
  private static readonly DOMAIN_URL = import.meta.env.VITE_BARZHA_DOMAIN_URL;
  private static readonly WIDGET_ID = import.meta.env.VITE_BARZHA_WIDGET_ID;
  private static initialized = false;
  private static ads: BarzhaClickAd[] = [];

  static async initialize(): Promise<boolean> {
    try {
      await this.sendUserVisit();
      this.initialized = true;
      return true;
    } catch {
      return false;
    }
  }

  private static async sendUserVisit(): Promise<void> {
    const url = `${this.DOMAIN_URL}/api/exchange/visit/${this.WIDGET_ID}`;
    const formData = this.getUserDataAsFormData();
    
    try {
      await fetch(url, {
        method: 'POST',
        body: formData
      });
    } catch {}
  }

  static async getAds(): Promise<BarzhaClickAd[]> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      // Создаем Promise для таймаута
      const timeout = new Promise<BarzhaClickAd[]>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Таймаут загрузки заданий BarzhaClick (3 сек)'));
        }, 3000);
      });

      // Race между получением заданий и таймаутом
      const ads = await Promise.race([
        this.fetchAds(),
        timeout
      ]).catch(error => {
        console.warn('⚠️ BarzhaClick:', error.message);
        return []; // Возвращаем пустой массив в случае таймаута
      });

      // Получаем статусы заданий
      const taskStatuses = await getCheckStatus();

      const updatedAds = this.updateAdsWithStatuses(ads, taskStatuses);
      
      if (updatedAds.length > 0) {
        this.ads = updatedAds;
        return updatedAds;
      } else {
        return [];
      }
    } catch {
      return [];
    }
  }

  private static async fetchAds(): Promise<BarzhaClickAd[]> {
    const url = `${this.DOMAIN_URL}/api/exchange/view/${this.WIDGET_ID}`;
    const formData = this.getUserDataAsFormData();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });
      
      const data: BarzhaClickResponse = await response.json();
      
      if (data.status === 'success') {
        let allAds: BarzhaClickAd[] = [];
        
        if (data.data && data.data.length > 0) {
          const newAds = data.data.map(ad => ({
            id: ad.exchange_ad_id || 0,
            title: ad.title || '',
            description: ad.description || '',
            image_url: ad.image_href || '',
            reward_amount: ad.reward_amount || 1,
            reward_type: ad.reward_type || 'tickets'
          }));
          allAds = [...allAds, ...newAds];
        }
        
        if (data.started_tasks && data.started_tasks.length > 0) {
          const startedAds = data.started_tasks.map(ad => ({
            id: ad.exchange_ad_id || 0,
            title: ad.title || '',
            description: ad.description || '',
            image_url: ad.image_href || '',
            reward_amount: ad.reward_amount || 1,
            reward_type: ad.reward_type || 'tickets',
            status: ad.status || 'in_progress'
          }));
          allAds = [...allAds, ...startedAds];
        }
        
        return allAds;
      } else {
        return [];
      }
    } catch {
      return [];
    }
  }

  private static updateAdsWithStatuses(ads: BarzhaClickAd[], taskStatuses: TaskStatusItem[]): BarzhaClickAd[] {
    if (!taskStatuses || !Array.isArray(taskStatuses)) {
      return ads;
    }
    
    const exchangeStatuses = taskStatuses.filter(status => status.type === 'exchange');
    
    if (exchangeStatuses.length === 0) {
      return ads;
    }
    
    const updatedAds = ads.map(ad => {
      const statusItem = exchangeStatuses.find(status => status.task_id === ad.id.toString());
      
      if (statusItem) {
        return {
          ...ad,
          status: statusItem.status,
          visibility: statusItem.visibility
        };
      }
      
      return ad;
    });
    
    return updatedAds.filter(ad => ad.visibility !== false);
  }

  static async handleClick(adId: number): Promise<string | null> {
    if (!this.initialized) {
      await this.initialize();
    }
    
    const url = `${this.DOMAIN_URL}/api/exchange/click/${this.WIDGET_ID}/${adId}`;
    const formData = this.getUserDataAsFormData();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });
      
      if (response.status !== 200) {
        throw new Error("Нет ответа от URL объявления");
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        return data.data;
      } else {
        return null;
      }
    } catch {
      return null;
    }
  }

  static async claimReward(adId: number): Promise<boolean> {
    try {
      const response = await claimBarzaTaskExchange(adId);
      
      if (response && (response.type === 'tickets' || response.amount > 0)) {
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  private static getUserDataAsFormData(): FormData {
    const webApp = window?.Telegram?.WebApp;
    const tgUser = webApp?.initDataUnsafe?.user;

    const formData = new FormData();
    formData.append('tg_user_id', tgUser?.id?.toString() || '');
    formData.append('tg_user_first_name', tgUser?.first_name || '');
    formData.append('tg_user_last_name', tgUser?.last_name || '');
    formData.append('tg_user_locale', tgUser?.language_code || '');
    formData.append('tg_user_is_premium', tgUser?.is_premium ? 'true' : 'false');
    formData.append('tg_user_device', webApp?.platform || '');

    return formData;
  }
}
